import type { ComponentProps, ReactNode } from "react";

/**
 * Le bouton, recopié des trois rangs du proto école — `ButtonPrimary`,
 * `ButtonSecondary`, `ButtonTertiary` — ramenés à un composant à variantes.
 *
 * UN composant plutôt que trois : ce dépôt n'a pas les onze variantes du proto
 * (Quaternary, Quinary, Octonary…), et trois fichiers pour trois jeux de classes
 * ne feraient que trois endroits où le référentiel peut dériver. C'est aussi la
 * SEULE source des classes de bouton : `LinkButton` les prend ici, par
 * `buttonClasses`, au lieu d'en tenir une copie qui avait déjà dérivé.
 *
 * ⚠️ Les VALEURS sont celles du proto, pas les nôtres. Le changement qui se voit
 * le plus : le rang primaire est un fond `mg-nav` (#263171, l'aplat de
 * navigation) qui s'ÉCLAIRCIT vers `mg-action` (#455f9b) au survol. On faisait
 * l'inverse — fond action, survol assombri par un filtre — et c'est ce qui donnait
 * à tous les boutons leur air de brouillon.
 *
 * `accent` n'apparaît nulle part ici : c'est la règle du référentiel, l'accent est
 * esthétique et catégoriel, jamais une action.
 */

export type ButtonTone = "primary" | "secondary" | "tertiary";
export type ButtonSize = "sm" | "md";

/**
 * ButtonPrimary : `bg-mg-nav text-white hover:bg-mg-action`, désactivé en
 * `bg-mg-subtle text-mg-secondary`. Le proto écrit `text-mg-text-muted` : 2,98:1 sur
 * ce fond, illisible. Un bouton désactivé doit encore se lire, sinon on ne sait pas
 * ce qu'il ferait ; `mg-secondary` y tient 6,58:1, et le fond suffit à dire l'état.
 *
 * ButtonSecondary : trait et texte dans la couleur d'action, fond `mg-hover` au
 * survol. ⚠️ Pas de `hover:*-mg-action-hover` : ce token n'est pas dans
 * `BRANDABLE_VARS`, donc chez une école à marque le survol repassait au bleu
 * Mentor Goal. Seul le fond change, la couleur de l'école reste.
 *
 * ButtonTertiary : l'icône seule, `bg-mg-surface shadow-lg hover:bg-mg-subtle`.
 */
const TONE: Record<ButtonTone, string> = {
	primary:
		"bg-mg-nav text-mg-inverse border border-transparent hover:bg-mg-action disabled:bg-mg-subtle disabled:text-mg-secondary",
	secondary:
		"bg-transparent text-mg-action border border-mg-action hover:bg-mg-hover disabled:border-mg-border-strong disabled:text-mg-secondary",
	tertiary:
		"bg-mg-surface text-mg-icon-secondary border border-transparent shadow-lg hover:bg-mg-subtle disabled:bg-mg-page",
};

/**
 * `large` = `p-xsm` (8px) autour, libellé `px-xsm text-xsm` (8px, 16/24) : soit
 * 40px de haut, la hauteur d'un champ. `small` = `p-xxsm` (4px), libellé
 * `px-xxsm text-xxsm` (4px, 14/20).
 *
 * ⚠️ Ces hauteurs ne valent qu'à partir de `md`. Sous `md`, tout bouton fait 44px
 * (`min-h-11`), la cible d'un pouce : à 32 et 40px, aucune action de l'espace
 * école ne l'atteignait en 390. Le proto, écrit pour un écran de bureau, n'a pas
 * cette contrainte ; ici l'entreprise comme le référent ouvrent le lien du mail
 * sur leur téléphone.
 */
const SIZE: Record<ButtonSize, string> = {
	sm: "min-h-11 md:min-h-8 p-1 gap-1 [&>span]:px-1",
	md: "min-h-11 md:min-h-field p-2 gap-2 [&>span]:px-2",
};

/** Le bouton d'icône seule reste carré : sa largeur suit sa hauteur, 44px sous `md`. */
const ICON_ONLY: Record<ButtonSize, string> = {
	sm: "min-w-11 md:min-w-8",
	md: "min-w-11 md:min-w-10",
};

/**
 * La taille du libellé, posée SUR le span dans le JSX : les `t-*` sont des
 * classes de l'échelle (`tokens.css`), pas des utilitaires, donc un
 * `[&>span]:t-button` ne compilerait pas.
 */
const LABEL: Record<ButtonSize, string> = {
	sm: "t-button",
	md: "t-button-lg",
};

export function buttonClasses({
	tone = "secondary",
	size = "md",
	full = false,
	className = "",
}: {
	tone?: ButtonTone;
	size?: ButtonSize;
	full?: boolean;
	className?: string;
}): string {
	return `inline-flex w-fit items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed ${TONE[tone]} ${SIZE[size]} ${full ? "w-full!" : ""} ${className}`;
}

export function buttonLabelClasses(size: ButtonSize = "md"): string {
	return `${LABEL[size]} whitespace-nowrap`;
}

export function Button({
	tone = "secondary",
	size = "md",
	icon,
	full = false,
	children,
	className = "",
	...rest
}: {
	tone?: ButtonTone;
	size?: ButtonSize;
	icon?: ReactNode;
	full?: boolean;
	children?: ReactNode;
} & ComponentProps<"button">) {
	return (
		<button
			type="button"
			{...rest}
			className={buttonClasses({
				tone,
				size,
				full,
				className: children ? className : `${ICON_ONLY[size]} ${className}`,
			})}
		>
			{icon}
			{/*
			 * Le libellé porte sa propre taille et son propre padding, comme au proto :
			 * c'est ce qui permet à un bouton d'icône seule de rester carré.
			 */}
			{children ? <span className={buttonLabelClasses(size)}>{children}</span> : null}
		</button>
	);
}
